import type { ReactNode } from 'react';
import React, { Component } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { Button, Text, Card } from '@terra/ui-design-system';
import { reportTelemetryError } from '@terra/ui-telemetry';
import { Alert01Icon, RefreshIcon } from '@terra/ui-icons';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  scopeId?: string;
  onClose?: () => void;
  dismissActionLabel?: string;
};
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    reportTelemetryError(error, {
      errorType: 'ErrorBoundary',
      cause: error.cause,
      message: error.message,
      errorInfo,
      scopeId: this.props.scopeId,
    });
    this.setState({
      error,
      errorInfo,
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.scopeId !== this.props.scopeId) {
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
      });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleDismiss = () => {
    this.handleReset();
    this.props.onClose?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          p="6"
          h="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Card.Root maxW="md" w="full">
            <Card.Body p="6">
              <VStack gap="4" alignItems="center" textAlign="center">
                <Box
                  w="12"
                  h="12"
                  borderRadius="full"
                  bg="bg.subtle"
                  borderWidth="1px"
                  borderColor="border.error"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="fg.error"
                >
                  <Alert01Icon size={24} />
                </Box>

                <VStack gap="2" alignItems="center">
                  <Text fontSize="lg" fontWeight="bold" color="fg.default">
                    Something went wrong
                  </Text>
                  <Text color="fg.muted" fontSize="sm">
                    An unexpected error occurred in this component. This error
                    has been logged for debugging.
                  </Text>
                </VStack>

                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Box
                    w="full"
                    p="3"
                    bg="bg.subtle"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border.error"
                  >
                    <Text fontSize="xs" fontFamily="mono" color="fg.error">
                      {this.state.error.message}
                    </Text>
                  </Box>
                )}

                <Box display="flex" gap="2">
                  <Button onClick={this.handleReset} size="sm" gap="2">
                    <RefreshIcon size={16} />
                    Try Again
                  </Button>
                  {this.props.onClose ? (
                    <Button
                      onClick={this.handleDismiss}
                      size="sm"
                      variant="outline"
                    >
                      {this.props.dismissActionLabel ?? 'Close tab'}
                    </Button>
                  ) : null}
                </Box>
              </VStack>
            </Card.Body>
          </Card.Root>
        </Box>
      );
    }

    return this.props.children;
  }
}
// Hook for functional components to handle errors
export const useErrorHandler = () => {
  return (error: Error, errorInfo?: React.ErrorInfo) => {
    console.error('Component error:', error, errorInfo);
    reportTelemetryError(error, {
      errorType: 'useErrorHandler',
      message: error.message,
      errorInfo,
    });
  };
};
